<script setup lang="ts">
  import { onMounted, ref, type Ref } from 'vue'
  import Calendar from '@/components/ui/calendar/Calendar.vue'
  import { CalendarDate, type DateValue, getLocalTimeZone, today } from '@internationalized/date'
  import { CalendarIcon } from 'lucide-vue-next'
  import InputField from '@/components/admin/form/InputField.vue'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { useTranslation } from '@/composables/useTranslation'
  import { useFieldValidation } from '@/composables/useQuestionnaire'

  const { t } = useTranslation({
    en: {
      dateFormat: 'DD.MM.YYYY'
    },
    de: {
      dateFormat: 'TT.MM.JJJJ'
    }
  })

  const props = defineProps({
    field: {
      type: Object,
      default: () => ({})
    },
    modelValue: {
      type: String,
      default: ''
    }
  })

  const emit = defineEmits(['change', 'update:modelValue', 'validation-error'])

  const { validationError, handleInputChange, validate } = useFieldValidation(props, emit)

  const selectedValue = ref(today(getLocalTimeZone())) as Ref<DateValue | null>
  const isOpen = ref(false)
  const inputValue = ref('')

  function parseDate(dateString: string): DateValue {
    if (!dateString || dateString.trim() === '') {
      return today(getLocalTimeZone())
    }

    // Handle DD.MM.YYYY format
    if (dateString.includes('.')) {
      const parts = dateString.split('.')
      if (parts.length === 3) {
        const day = parseInt(parts[0])
        const month = parseInt(parts[1])
        const year = parseInt(parts[2])

        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          try {
            return new CalendarDate(year, month, day)
          } catch (error) {
            return today(getLocalTimeZone())
          }
        }
      }
    }

    // Fallback to standard Date parsing
    const parsedDate = new Date(dateString)
    if (isNaN(parsedDate.getTime())) {
      return today(getLocalTimeZone())
    }

    const nc = new CalendarDate(
      parsedDate.getFullYear(),
      parsedDate.getMonth() + 1,
      parsedDate.getDate()
    )
    return nc
  }

  function emitChanges(newValue: DateValue | null) {
    if (!newValue) {
      inputValue.value = ''
      handleInputChange('')
      emit('change', '')
      return
    }

    const formattedDate = `${newValue.day.toString().padStart(2, '0')}.${newValue.month.toString().padStart(2, '0')}.${newValue.year}`
    inputValue.value = formattedDate
    if (formattedDate !== props.modelValue) {
      handleInputChange(formattedDate)
      emit('change', formattedDate)
    }
    isOpen.value = false
  }

  function handleKeydown(event: KeyboardEvent) {
    // Allow: backspace, delete, tab, escape, enter, and navigation keys
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End'
    ]
    if (allowedKeys.includes(event.code)) {
      return
    }

    // Allow dots
    if (event.key === '.') {
      return
    }

    // Only allow digits
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault()
    }
  }

  function handleManualInput(value: string) {
    if (!value) {
      inputValue.value = ''
      handleInputChange('')
      return
    }

    let formattedValue = value.replace(/[^\d]/g, '') // Remove non-digits

    if (formattedValue.length >= 2 && !formattedValue.includes('.')) {
      formattedValue = formattedValue.slice(0, 2) + '.' + formattedValue.slice(2)
    }
    if (formattedValue.length >= 5 && formattedValue.split('.').length === 2) {
      formattedValue = formattedValue.slice(0, 5) + '.' + formattedValue.slice(5)
    }

    if (formattedValue.length > 10) {
      formattedValue = formattedValue.slice(0, 10)
    }

    inputValue.value = formattedValue

    handleInputChange(formattedValue)

    if (formattedValue.length === 10) {
      const parts = formattedValue.split('.')
      if (parts.length === 3) {
        const day = parseInt(parts[0])
        const month = parseInt(parts[1])
        const year = parseInt(parts[2])

        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          try {
            const dateValue = new CalendarDate(year, month, day)
            selectedValue.value = dateValue
            emitChanges(dateValue)
          } catch (error) {
            // Invalid date, don't update selectedValue
          }
        }
      }
    }
  }

  function formatDisplayDate(dateValue: DateValue): string {
    return `${dateValue.day.toString().padStart(2, '0')}.${dateValue.month.toString().padStart(2, '0')}.${dateValue.year}`
  }

  function getDatePlaceholder(): string {
    return t('dateFormat')
  }

  function validateCurrentValue() {
    const result = validate(inputValue.value)
    validationError.value = result.errors[0] || ''
    emit('validation-error', !result.valid)
  }

  onMounted(() => {
    if (props.modelValue && props.modelValue.trim() !== '') {
      let dateValues = props.modelValue.split(',')
      if (dateValues.length > 0 && dateValues[0].trim() !== '') {
        const parsedDate = parseDate(dateValues[0])
        selectedValue.value = parsedDate
        inputValue.value = formatDisplayDate(selectedValue.value)
      }
    }
    const inputElement = document.querySelector(
      `input[name="${props.field.uuid}"]`
    ) as HTMLInputElement
    if (inputElement) {
      inputElement.focus()
    }
  })
</script>

<template>
  <div class="relative w-full">
    <InputField
      :id="field.uuid"
      :name="field.uuid"
      :model-value="inputValue"
      :placeholder="getDatePlaceholder()"
      :disabled="false"
      :error="validationError"
      class="w-full"
      :data-cy="`CalendarV3DateInput-${field.uuid}`"
      @input="handleManualInput"
      @keydown="handleKeydown"
      @blur="validateCurrentValue"
    />
    <Popover v-model:open="isOpen">
      <PopoverTrigger as-child>
        <CalendarIcon
          :class="[
            'absolute h-4 w-4 cursor-pointer opacity-50 hover:opacity-75',
            validationError ? 'right-12 top-3' : 'right-3 top-3'
          ]"
        />
      </PopoverTrigger>
      <PopoverContent
        class="mt-1 w-auto translate-x-3 p-0"
        align="end"
      >
        <Calendar
          v-model="selectedValue"
          :input-field="false"
          :is-date-disabled="() => false"
          @update:model-value="emitChanges"
        />
      </PopoverContent>
    </Popover>
  </div>
</template>
